import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabase";
import { findRecipe } from "../data/recipes";
import { useUserRecipes } from "./useUserRecipes";
import { readCache, writeCache } from "./useCachedQuery";
import { findOvernightStepIndex } from "./calendarUtils";

// useActiveCookSession — app-level "is there a cook in progress?" query.
//
// Reads cook_sessions (migration 0136) + joins the most-recent open
// cook_session_steps row so the banner knows which step is live, and
// the earliest-undelivered cook_step_notifications row so the banner
// can render an accurate mm:ss countdown tied to the same deadline
// that the server-side pg_cron drain fires on.
//
// Why a dedicated hook instead of reading from useCookTelemetry: the
// telemetry hook is OWNED by CookMode (it writes). The banner needs a
// READ view that stays live whether CookMode is mounted or not — the
// whole point is "user closed the cook view, banner still knows a cook
// is active." This hook subscribes via realtime so taps in another tab
// / the server-side drain deleting a row propagate without a refresh.
//
// Contract:
//   { session, recipe, activeStep, timerEndsAt, dismissed, dismiss,
//     undismiss, refresh, loading }
//
//   session        cook_sessions row, or null
//   recipe         resolved recipe object (bundled or user_recipes), or null
//   activeStep     the live cook_session_steps row (finished_at IS NULL), or null
//   timerEndsAt    ISO timestamp of the earliest pending cook_step_notification
//                  for this session, or null
//   dismissed      user hit the × on the banner in THIS tab session (in-memory)
//   dismiss/undismiss  control the above
//   refresh        force re-query (e.g. after CookMode explicitly ends cook)
//   loading        initial-load flag so the banner doesn't flicker

const ACTIVE_WINDOW_MS    = 2  * 60 * 60 * 1000; // floor: 2h after start
// Overnight bakes need a much wider window — the user kneads at 8 PM,
// goes to sleep, wakes up at 7 AM and laps the rest into shape +
// proof + bake. We need the session to stay banner-live through the
// full Day 2 morning. 30 hours covers a worst-case 8 PM → next
// morning's noon eat time, plus slack.
const OVERNIGHT_WINDOW_MS = 30 * 60 * 60 * 1000;
// Server-side query cutoff for the LIVE banner candidates. We don't
// yet know if the session is overnight (we haven't resolved the
// recipe), so we cast a wide net and filter client-side after the
// recipe is in hand.
const QUERY_CUTOFF_MS     = OVERNIGHT_WINDOW_MS;
// A cook is NEVER closed by a timer — only by the user. Sessions
// past their live window stop being the pinned banner but surface as
// LAPSED ("Did you finish X? Log it / didn't cook it") until the
// user resolves them, for up to 7 days. Before this, a session past
// its window simply vanished — no cook_log, no prompt, three days of
// pancakes silently never recorded.
const LAPSED_WINDOW_MS    = 7 * 24 * 60 * 60 * 1000;

// Decide which LIVE window applies to a given session. Overnight
// recipes (any step has overnight: true) get the long window;
// everything else scales with the recipe's OWN stated time — a 4-hour
// braise stays banner-live for 12 hours instead of losing its resume
// affordance mid-cook at the old flat 2h cap. Floor 2h, ceiling 30h.
// Exported: CookMode uses the same window to schedule the server-side
// "did you finish this?" push so the OS nudge and the in-app lapsed
// banner flip at the same moment.
export function windowForRecipe(recipe) {
  if (!recipe?.steps) return ACTIVE_WINDOW_MS;
  if (findOvernightStepIndex(recipe.steps) >= 0) return OVERNIGHT_WINDOW_MS;
  const totalMin = (Number(recipe?.time?.prep) || 0) + (Number(recipe?.time?.cook) || 0);
  const scaled = totalMin > 0 ? totalMin * 60 * 1000 * 3 : 0;
  return Math.min(OVERNIGHT_WINDOW_MS, Math.max(ACTIVE_WINDOW_MS, scaled));
}

// Offline cache — mirrors the active session + recipe + step + timer
// to localStorage so that a cold reload during disconnect still shows
// the banner with a correct ticking timer. TTL stretches to cover
// overnight sessions; the read-side validity check below filters out
// anything past the recipe-appropriate window so we never display a
// ghost session that's actually expired server-side.
const ACTIVE_CACHE_KEY = (uid) => `activeCook:v1:${uid}`;
const ACTIVE_CACHE_TTL_MS = OVERNIGHT_WINDOW_MS + 30 * 60 * 1000;

// Read the cache and prune anything past the recipe's resume window.
// Returns null if there's nothing valid to hydrate from. Returning the
// cached recipe alongside means a tap-to-resume during disconnect
// can still open CookMode without a network round-trip.
function readActiveCookCache(uid) {
  if (!uid) return null;
  const cached = readCache(ACTIVE_CACHE_KEY(uid), ACTIVE_CACHE_TTL_MS);
  if (!cached) return null;
  const startedAt = cached.session?.started_at
    ? new Date(cached.session.started_at).getTime()
    : 0;
  if (!startedAt) return null;
  const window = windowForRecipe(cached.recipe);
  if (Date.now() - startedAt > window) return null;
  return cached;
}

export function useActiveCookSession(userId, focusedSessionId = null) {
  // Hydrate state from the localStorage cache synchronously in the
  // useState initializers — no flicker between "blank banner" and
  // "cached banner" on cold reload, AND offline reload still shows
  // the live cook with an accurate ticking timer.
  const cachedHydrate = userId ? readActiveCookCache(userId) : null;
  const [session, setSession]         = useState(() => cachedHydrate?.session || null);
  const [activeStep, setActiveStep]   = useState(() => cachedHydrate?.activeStep || null);
  const [timerEndsAt, setTimerEndsAt] = useState(() => cachedHydrate?.timerEndsAt || null);
  // The recipe is cached alongside the session so resume-from-banner
  // works offline (useUserRecipes' own cache may not hold the row,
  // and the supabase fallback in App.resumeCookFromBanner will also
  // fail offline). Live resolution still wins when available.
  const [cachedRecipe, setCachedRecipe] = useState(() => cachedHydrate?.recipe || null);
  const [dismissed, setDismissed]     = useState(false);
  const [loading, setLoading]         = useState(() => !cachedHydrate);
  const [tick, setTick]               = useState(0);  // manual refresh trigger
  // Lapsed sessions — own cooks past their live window but unresolved
  // (status still 'active', ≤7 days old). Entries: { session, recipe }.
  // lapsedHidden holds session ids the user ✕'d THIS tab session (or
  // just discarded — instant hide while the abandon write settles).
  const [lapsedSessions, setLapsedSessions] = useState([]);
  const [lapsedHidden, setLapsedHidden]     = useState(() => new Set());

  // Resolver for user-authored recipes (custom / AI). findRecipe checks
  // bundled first, falls through to the user resolver — matches the
  // pattern used in CreateMenu / Plan.
  const { findBySlug: findUserRecipe } = useUserRecipes(userId);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  // After an explicit abandon, suppress the query for HOLD_MS to keep
  // a stale read (realtime UPDATE event delivered before the row's
  // commit propagates to the read replica, OR a subsequent poll
  // landing while the sweep hadn't finished yet) from re-populating
  // the banner with the abandoned session. 3 seconds is plenty for
  // Supabase to settle and the realtime channel to deliver the
  // status=abandoned event.
  const HOLD_MS = 3000;
  const holdUntilRef = useRef(0);

  // ── Query ─────────────────────────────────────────────────────────
  // Fetch the most-recent active session for this user within the
  // 2-hour resume window. Filtering at the DB side keeps us honest
  // about which sessions are banner-worthy.
  useEffect(() => {
    if (!userId) { setSession(null); setActiveStep(null); setTimerEndsAt(null); setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        // Two cutoffs: LIVE candidates use the 30h ceiling (filtered
        // down to the recipe-appropriate window below); OWN sessions
        // additionally come back through the 7-day lapsed window so
        // an unresolved cook surfaces as "did you finish this?"
        // instead of silently vanishing.
        const liveCutoff   = new Date(Date.now() - QUERY_CUTOFF_MS).toISOString();
        const lapsedCutoff = new Date(Date.now() - LAPSED_WINDOW_MS).toISOString();

        // OWN sessions — limit lifted to 10 so the user-facing
        // banner can pick the focused cook (by focusedSessionId)
        // out of multiple parallel cooks. Pre-multi-cook this was
        // limit(1); now with banner-as-switcher we need the full
        // set so the banner can render tabs for each.
        const ownReq = supabase
          .from("cook_sessions")
          .select("id, recipe_slug, recipe_title, recipe_emoji, started_at, status, user_id, step_summary, servings, cook_params")
          .eq("user_id", userId)
          .eq("status", "active")
          .gte("started_at", lapsedCutoff)
          .order("started_at", { ascending: false })
          .limit(10);

        // TEAM-COOK sessions — sessions where the viewer is a
        // participant (helper/diner). Family-RLS lets them SELECT
        // the joined cook_sessions row. Lapsed closure is the
        // OWNER's job, so team rows stay on the live cutoff.
        const teamReq = supabase
          .from("cook_session_participants")
          .select("cook_sessions!inner(id, recipe_slug, recipe_title, recipe_emoji, started_at, status, user_id, step_summary, servings, cook_params)")
          .eq("user_id", userId)
          .eq("cook_sessions.status", "active")
          .gte("cook_sessions.started_at", liveCutoff)
          .order("cook_sessions(started_at)", { ascending: false })
          .limit(3);

        const [ownRes, teamRes] = await Promise.all([ownReq, teamReq]);
        if (!alive) return;
        if (ownRes.error) console.warn("[activeCook] own query:", ownRes.error.message);
        if (teamRes.error) console.warn("[activeCook] team query:", teamRes.error.message);

        const own = ownRes.data || [];
        // Split own rows by their recipe-aware window: live candidates
        // power the pinned banner; everything past its window (but
        // status still 'active') is LAPSED — unresolved, awaiting the
        // user's log-it-or-discard call. Sync recipe resolution is
        // reliable for own rows (bundled + the viewer's own library).
        const nowMs = Date.now();
        const ownLive = [];
        const ownLapsed = [];
        for (const row of own) {
          const ageMs = nowMs - new Date(row.started_at).getTime();
          const rec = findRecipe(row.recipe_slug, findUserRecipe, userId) || null;
          if (ageMs <= windowForRecipe(rec)) ownLive.push(row);
          else ownLapsed.push({ session: row, recipe: rec });
        }
        const teamSessions = (teamRes.data || [])
          .map((r) => r.cook_sessions)
          .filter((s) => s && s.status === "active" && s.user_id !== userId);
        const allSessions = [...ownLive, ...teamSessions]
          .filter(Boolean)
          .sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
        // Pick the focused session if the caller provided one (the
        // banner is the source of truth in multi-cook scenarios);
        // otherwise fall back to most-recent.
        const focused = focusedSessionId
          ? allSessions.find((x) => x.id === focusedSessionId)
          : null;
        const sessions = focused ? [focused] : allSessions.slice(0, 1);
        const error = null;
        if (!alive) return;
        if (error) { console.warn("[activeCook] session query:", error.message); setLoading(false); return; }
        // Hold-window: if the user just hit explicit abandon, ignore
        // any session row this query returns. The DB sweep is in flight
        // (or just settled) — the read replica or realtime cache may
        // briefly still see status='active' on the row we just flipped.
        // Without this, the banner repopulates 0-5s after the abandon
        // and re-disappears once the next refresh fires. Net effect to
        // the user: banner "comes back" briefly. Hold suppresses that.
        if (Date.now() < holdUntilRef.current) {
          // eslint-disable-next-line no-console
          console.log("[activeCook] query suppressed by hold window");
          setLoading(false);
          return;
        }
        setLapsedSessions(ownLapsed);
        let s = sessions?.[0] || null;
        // Recipe-aware cutoff — non-overnight sessions older than 2h
        // shouldn't bring the banner back even though we cast a wider
        // server-side net. Overnight sessions stay live through the
        // 30h window so the morning push has something to resume.
        if (s) {
          const startedAtMs = new Date(s.started_at).getTime();
          const ageMs = Date.now() - startedAtMs;
          // Recipe resolution: when we're a PARTICIPANT (the
          // session is owned by someone else), prefer the session
          // owner's user_recipe over any bundled match. The bundled
          // lookup falls back only if the cross-user fetch fails.
          // This prevents slug collisions like "(Not-So-Authentic)
          // Spaghetti Carbonara" resolving to the bundled
          // "Spaghetti Carbonara" recipe.
          let sessionRecipe = null;
          if (s.user_id && s.user_id !== userId) {
            try {
              const { data: ur } = await supabase
                .from("user_recipes")
                .select("recipe, slug")
                .eq("slug", s.recipe_slug)
                .eq("user_id", s.user_id)
                .maybeSingle();
              if (ur?.recipe) {
                sessionRecipe = { ...ur.recipe, slug: ur.slug, _userId: s.user_id };
              }
            } catch (e) {
              console.warn("[activeCook] team recipe fetch:", e?.message || e);
            }
          }
          if (!sessionRecipe) {
            sessionRecipe = findRecipe(s.recipe_slug, findUserRecipe, userId);
          }
          // Slug missed — match by the session's recipe TITLE. Slugs drift
          // (re-save suffixes "-2", a background stub races a foreground
          // save), but the title is stable. Without this the banner shows
          // "0/0 steps" and resume reconstructs from a prose-less snapshot.
          if (!sessionRecipe && s.recipe_title) {
            try {
              const { data: byTitle } = await supabase
                .from("user_recipes")
                .select("recipe, slug")
                .eq("user_id", userId)
                .eq("recipe->>title", s.recipe_title)
                .order("updated_at", { ascending: false })
                .limit(1);
              if (byTitle?.[0]?.recipe) {
                sessionRecipe = { ...byTitle[0].recipe, slug: byTitle[0].slug };
              }
            } catch (e) {
              console.warn("[activeCook] title fallback threw:", e?.message || e);
            }
          }
          const window = windowForRecipe(sessionRecipe);
          if (ageMs > window) {
            // eslint-disable-next-line no-console
            console.log("[activeCook] session past recipe-aware cutoff", { ageMs, window, slug: s.recipe_slug });
            s = null;
          } else if (sessionRecipe) {
            setCachedRecipe(sessionRecipe);
          }
        }
        // eslint-disable-next-line no-console
        console.log("[activeCook] query result", { hasSession: !!s, sessionId: s?.id, status: s?.status, slug: s?.recipe_slug, tick });
        setSession(s);
        if (!s) {
          setActiveStep(null);
          setTimerEndsAt(null);
          setCachedRecipe(null);
          // Server says no active session — clear the cache so the
          // next cold reload doesn't briefly flash a ghost banner.
          writeCache(ACTIVE_CACHE_KEY(userId), null);
          setLoading(false);
          return;
        }

        // Live step ONLY — include timer_state so we can pull endsAt
        // straight from the row that the user actually started.
        // Previously we used cook_step_notifications.deliver_at, but
        // that row is queued the MOMENT the user navigates into a
        // step (startStep auto-queues a push for now + nominal),
        // BEFORE the user has tapped Start. The banner then treated
        // step entry as "timer running" and ticked the bar down even
        // though no timer was actually started. timer_state.endsAt
        // is only written by markTimerStarted (user-initiated), so
        // it's the right "is the timer actually ticking?" signal.
        const { data: stepRows } = await supabase
          .from("cook_session_steps")
          .select("id, step_id, step_title, started_at, finished_at, nominal_seconds, timer_state")
          .eq("cook_session_id", s.id)
          .is("finished_at", null)
          .order("started_at", { ascending: false })
          .limit(1);
        if (!alive) return;
        const row = stepRows?.[0] || null;
        setActiveStep(row);
        const tsEndsAt = row?.timer_state?.endsAt;
        const tsPaused = !!row?.timer_state?.isPaused;
        setTimerEndsAt(tsEndsAt && !tsPaused ? tsEndsAt : null);
        setLoading(false);
      } catch (e) {
        console.warn("[activeCook] refresh threw:", e?.message || e);
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, tick, focusedSessionId]);

  // ── Realtime ──────────────────────────────────────────────────────
  // Tail changes to all three relevant tables so the banner converges
  // without a poll. One channel, three filters. Any event just triggers
  // a refresh — simpler than threading the row into state, and these
  // events are rare (a few per cook).
  useEffect(() => {
    if (!userId) return undefined;
    const channel = supabase
      .channel(`active-cook:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_sessions", filter: `user_id=eq.${userId}` },
        () => refresh(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps", filter: `user_id=eq.${userId}` },
        () => refresh(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_step_notifications", filter: `user_id=eq.${userId}` },
        () => refresh(),
      )
      // Team-cook: refresh when I'm added/removed from a participant
      // row, or when a session I'm a participant on transitions.
      // We can't pre-filter cook_sessions to "sessions I'm a part of"
      // because the filter API requires equality, so we listen
      // broadly to participants joined to me and to all cook_sessions
      // status changes (refresh debounces the duplicates).
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_participants", filter: `user_id=eq.${userId}` },
        () => refresh(),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId, refresh]);

  // ── Poll fallback ─────────────────────────────────────────────────
  // Realtime channels drop on network blips — a poll ensures the
  // banner converges even if we missed a delete event. Stretched to
  // 5 minutes (was 30s) because realtime catches the live cases
  // reliably; the poll is just a safety net for the rare missed
  // event. Visibility-gated: when the tab is backgrounded, no poll
  // fires — when the tab returns to foreground, one runs immediately
  // to converge.
  useEffect(() => {
    if (!userId) return undefined;
    const POLL_MS = 5 * 60 * 1000;
    let id = null;
    const start = () => {
      if (id) return;
      id = setInterval(refresh, POLL_MS);
    };
    const stop = () => {
      if (id) { clearInterval(id); id = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refresh();      // converge immediately on foreground return
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [userId, refresh]);

  // Resolve the recipe object. findRecipe checks bundled, then user.
  // Falls back to the cached recipe when both fail — that path is
  // hit on offline cold-reload (useUserRecipes hasn't loaded yet
  // because supabase is unreachable). Cached row is keyed to the
  // current session.recipe_slug so we never serve a mismatched
  // recipe; if slugs disagree, the cache is ignored.
  const recipe = useMemo(() => {
    if (!session?.recipe_slug) return null;
    const live = findRecipe(session.recipe_slug, findUserRecipe);
    if (live) return live;
    if (cachedRecipe?.slug === session.recipe_slug) return cachedRecipe;
    return null;
  }, [session?.recipe_slug, findUserRecipe, cachedRecipe]);

  // Write through to cache whenever we have a consistent state
  // (session + matching recipe). Skipping when recipe is null avoids
  // caching a session whose recipe we couldn't resolve — we'd just
  // re-fetch it next mount anyway. The activeStep + timerEndsAt are
  // included so the banner renders correctly on offline cold-reload.
  useEffect(() => {
    if (!userId) return;
    if (!session) return;
    if (!recipe || recipe.slug !== session.recipe_slug) return;
    writeCache(ACTIVE_CACHE_KEY(userId), {
      session,
      recipe,
      activeStep,
      timerEndsAt,
    });
    // Also keep cachedRecipe state in sync — when the live resolver
    // wins (recipe came from useUserRecipes), the cache state above
    // might be stale on the next render. Sync both directions.
    setCachedRecipe(recipe);
  }, [userId, session, recipe, activeStep, timerEndsAt]);

  // Reset dismissal whenever the session identity changes — a new cook
  // deserves a fresh banner, even if the user dismissed the last one.
  const lastSessionIdRef = useRef(null);
  useEffect(() => {
    if (lastSessionIdRef.current !== session?.id) {
      setDismissed(false);
      lastSessionIdRef.current = session?.id || null;
    }
  }, [session?.id]);

  const dismiss   = useCallback(() => setDismissed(true), []);

  // Hide one lapsed prompt for this tab session — used by the ✕ and
  // by discard taps for instant UI while the abandon write settles
  // (the realtime refresh then removes it for real).
  const dismissLapsed = useCallback((sessionId) => {
    if (!sessionId) return;
    setLapsedHidden(prev => {
      const next = new Set(prev);
      next.add(sessionId);
      return next;
    });
  }, []);

  const visibleLapsed = useMemo(
    () => lapsedSessions.filter((l) => !lapsedHidden.has(l.session?.id)),
    [lapsedSessions, lapsedHidden],
  );

  // Explicit local-state clear used by the cook-mode EXIT path so the
  // banner disappears the instant the user confirms abandonment, even
  // if the DB UPDATE is mid-flight or the next refresh is still
  // reading a stale "active" row. The realtime subscription will
  // confirm shortly after; this just removes the visible lag.
  const clearLocal = useCallback(() => {
    holdUntilRef.current = Date.now() + HOLD_MS;
    setSession(null);
    setActiveStep(null);
    setTimerEndsAt(null);
    setCachedRecipe(null);
    if (userId) writeCache(ACTIVE_CACHE_KEY(userId), null);
  }, [userId]);
  const undismiss = useCallback(() => setDismissed(false), []);

  return {
    session,
    recipe,
    activeStep,
    timerEndsAt,
    dismissed,
    dismiss,
    undismiss,
    refresh,
    clearLocal,
    loading,
    // Lapsed closure — own cooks past their live window, unresolved.
    lapsedSessions: visibleLapsed,
    dismissLapsed,
  };
}
